import React, { FC } from 'react';
import { Link, Text } from '@blueprint/ui';

import {
    Content,
    StyledButton,
    StyledText,
    StyledLinkContainer,
    StyledImg,
    StyledHeading
} from './XhrResponseModalContent.styles';
import { StyledIcon } from '../AlertCtaModal/AlertCtaModal.styles';
import { getTranslator } from '../../shared/i18n/getTranslator';
import searchTranslations from 'public/locales/en/search.json';
import emailAlertsSvg from '../../public/assets/search/multiple-properties-email.svg';
import saveSearchSvg from '../../public/assets/search/multiple-properties-location.svg';
import errorSvg from '../../public/assets/search/alert-triangle-purple.svg';

export type ResponseType =
    | 'saveSearchSuccess'
    | 'saveAlertSuccess'
    | 'saveSearchFailure'
    | 'saveAlertFailure'
    | null;

export type Props = {
    responseType: ResponseType;
    close: () => void;
    action?: () => void;
};

const t = getTranslator(searchTranslations);

const successText = {
    title: t('ajaxResponseModal.success.title'),
    buttonText: t('ajaxResponseModal.success.buttonText'),
    note: t('ajaxResponseModal.success.note')
};

const saveSearchSuccessText = {
    heading: t('ajaxResponseModal.saveSearchSuccess.heading'),
    text: t('ajaxResponseModal.saveSearchSuccess.text'),
    linkText: t('ajaxResponseModal.saveSearchSuccess.linkText')
};

const saveAlertSuccessText = {
    heading: t('ajaxResponseModal.saveAlertSuccess.heading'),
    text: t('ajaxResponseModal.saveAlertSuccess.text'),
    linkText: t('ajaxResponseModal.saveAlertSuccess.linkText')
};

const failureText = {
    buttonText: t('ajaxResponseModal.failure.buttonText'),
    linkText: t('ajaxResponseModal.failure.linkText'),
    note: t('ajaxResponseModal.failure.note')
};

const saveSearchFailureText = {
    title: t('ajaxResponseModal.saveSearchFailure.title'),
    heading: t('ajaxResponseModal.saveSearchFailure.heading'),
    text: t('ajaxResponseModal.saveSearchFailure.text')
};

const saveAlertFailureText = {
    title: t('ajaxResponseModal.saveAlertFailure.title'),
    heading: t('ajaxResponseModal.saveAlertFailure.heading'),
    text: t('ajaxResponseModal.saveAlertFailure.text')
};

const getContentModel = (
    responseType: ResponseType,
    close: () => void,
    buttonAction?: () => void
) => {
    switch (responseType) {
        case 'saveSearchSuccess':
            return {
                ...successText,
                ...saveSearchSuccessText,
                image: saveSearchSvg,
                buttonAction: close,
                linkUrl: '/myaccount/alerts-searches/',
                linkAction: undefined,
                type: 'success'
            };
        case 'saveSearchFailure':
            return {
                ...failureText,
                ...saveSearchFailureText,
                image: errorSvg,
                buttonAction,
                linkUrl: undefined,
                linkAction: close,
                type: 'failure'
            };
        case 'saveAlertSuccess':
            return {
                ...successText,
                ...saveAlertSuccessText,
                image: emailAlertsSvg,
                buttonAction: close,
                linkUrl: '/myaccount/alerts-searches/',
                linkAction: undefined,
                type: 'success'
            };
        case 'saveAlertFailure':
        default:
            return {
                ...failureText,
                ...saveAlertFailureText,
                image: errorSvg,
                buttonAction,
                linkUrl: undefined,
                linkAction: close,
                type: 'failure'
            };
    }
};

export const XhrReponseModalContent: FC<Props> = ({
    responseType,
    close,
    action
}) => {
    const contentModel = getContentModel(responseType, close, action);

    return (
        <Content>
            <StyledImg src={contentModel.image} alt={contentModel.title} />
            <StyledHeading size="5">{contentModel.heading}</StyledHeading>
            <StyledText size="secondary">{contentModel.text}</StyledText>
            <StyledButton
                onClick={contentModel.buttonAction}
                color="primary-brand"
            >
                {contentModel.buttonText}
            </StyledButton>
            <StyledLinkContainer>
                {contentModel.type === 'success' ? (
                    <Link href={contentModel.linkUrl as string}>
                        {contentModel.linkText}
                    </Link>
                ) : (
                    <Link onClick={contentModel.linkAction} href="">
                        {contentModel.linkText}
                    </Link>
                )}
            </StyledLinkContainer>
            <Text size="small">
                <StyledIcon iconId="padlock" />
                {contentModel.note}
            </Text>
        </Content>
    );
};
